import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, FormHelperText } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { OptionsInterface } from '../../Common/types';
import { useDebounce } from '../../Common/utilities';
import { getUsers } from '../../User/usersApi';
import { UserListInterface } from '../../User/types';
import FormAutocompleteBox from '../../Common/FormItems/FormAutocompleteBox';
import { createRegisterRequest } from '../registerRequestApi';
import { useNotify } from '../../Common/snackbarHooks';
import FilterMultipleAutocomplete from '../../Common/FilterPopover/FilterMultipleAutocomplete';
import { HandleError } from '../../Common/ErrorHandling/ErrorHelper';

interface Props {
  isVisible: boolean;
  onClose: () => void;
}

const CreateRegisterRequestDialog: React.FC<Props> = (props: Props) => {
  const { notifySuccess, notifyError } = useNotify();
  const [warningText, setWarningText] = useState<string>('');
  const [secondaryWarningText, setSecondaryWarningText] = useState<string>('');
  const [disableCreateButton, setDisableCreateButton] = useState<boolean>(false);
  const [lastRegisterRequestId, setLastRegisterRequestId] = useState<number | null>(null);
  const [forceCreate, setForceCreate] = useState<boolean>(false);

  const [users, setUsers] = useState<UserListInterface[]>([]);
  const { loading, debounce } = useDebounce(
    (query: string) => getUsers({ query, hide_banned: true, page: 1 }),
    ({ data }) => setUsers(data.data),
    { runInitially: true, runInitiallyWith: '' }
  );

  const resetState = () => {
    setWarningText('');
    setSecondaryWarningText('');
    setDisableCreateButton(false);
    setLastRegisterRequestId(null);
    setForceCreate(false);
  };

  return (
    <Dialog
      open={props.isVisible}
      onClose={() => {
        resetState();
        props.onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <Formik
        initialValues={{ users: [] as UserListInterface[] }}
        validationSchema={Yup.object({
          users: Yup.array()
            .min(1, '* Användare är ett obligatoriskt fält')
            .required('* Användare är ett obligatoriskt fält'),
        })}
        onSubmit={(values, actions) => {
          const user = values.users[0];
          if (!user) return;

          createRegisterRequest({ user_id: user.id, force_create: forceCreate })
            .then(() => {
              notifySuccess(`RegU-begäran skapad för ${user.name}`);

              resetState();
              props.onClose();
            })
            .catch((error) => {
              const data = error.response?.data;
              const errors = data?.errors;

              const normalizedErrors = Array.isArray(errors) ? errors : [errors];

              const errorCodes = normalizedErrors
                .map((err) => (typeof err === 'string' ? err : err?.code))
                .filter(Boolean);

              const hasExistingInvalidRequest = errorCodes.includes('existing_invalid_request');
              const hasExistingValidRequest = errorCodes.includes('existing_valid_request');
              const hasUserUnder14YearsOld = errorCodes.includes('request_user_under_14_years_old');

              const existingRequestError = normalizedErrors.find((err) => {
                const code = typeof err === 'string' ? err : err?.code;

                return ['existing_invalid_request', 'existing_valid_request'].includes(code);
              });

              if (existingRequestError && typeof existingRequestError !== 'string') {
                setLastRegisterRequestId(existingRequestError.register_request_id);
              } else if (data?.register_request_id) {
                setLastRegisterRequestId(data.register_request_id);
              }

              if (errorCodes.length === 0) {
                setWarningText(`Det gick inte att skapa RegU-begäran: ${HandleError(data)}`);
                setSecondaryWarningText('');
                return;
              }

              if (hasExistingInvalidRequest) {
                const mainData = {
                  ...data,
                  errors: normalizedErrors.filter((err) => {
                    const code = typeof err === 'string' ? err : err?.code;

                    return code === 'existing_invalid_request';
                  }),
                };

                setWarningText(HandleError(mainData));
                setSecondaryWarningText('');
                setDisableCreateButton(true);
                setForceCreate(false);
                return;
              }

              if (hasExistingValidRequest) {
                const mainData = {
                  ...data,
                  errors: normalizedErrors.filter((err) => {
                    const code = typeof err === 'string' ? err : err?.code;

                    return code === 'existing_valid_request';
                  }),
                };

                const secondaryData = {
                  ...data,
                  errors: normalizedErrors.filter((err) => {
                    const code = typeof err === 'string' ? err : err?.code;

                    return code === 'request_user_under_14_years_old';
                  }),
                };

                setWarningText(HandleError(mainData));
                setSecondaryWarningText(hasUserUnder14YearsOld ? HandleError(secondaryData) : '');
                setDisableCreateButton(false);
                setForceCreate(true);
                return;
              }

              if (hasUserUnder14YearsOld) {
                setWarningText(HandleError(data));
                setSecondaryWarningText('');
                setDisableCreateButton(false);
                setForceCreate(true);
                return;
              }

              setWarningText(`Det gick inte att skapa RegU-begäran: ${HandleError(data)}`);
              setSecondaryWarningText('');
              setDisableCreateButton(false);
              setForceCreate(false);
            })
            .finally(() => actions.setSubmitting(false));
        }}
      >
        {({ values, errors, handleChange, setFieldValue, handleSubmit, isSubmitting, isValid, dirty }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle className="text-xl text-neutral-600 !font-bold">Skapa RegU-begäran</DialogTitle>
            <DialogContent className="!pt-4 !pb-6">
              <div className="text-neutral-600 text-sm">
                <FormControl fullWidth error={!!errors?.users}>
                  <FilterMultipleAutocomplete
                    id="users"
                    label="Sök användare"
                    placeholder="Skriv för att söka..."
                    groupBy={({ team_name }) => team_name}
                    options={users}
                    values={values.users}
                    loading={loading}
                    getOptionLabel={({ name }) => name}
                    onChange={(_, options) => {
                      setFieldValue('users', options.slice(-1));
                      resetState();
                    }}
                    onInputChange={(query) => debounce(query)}
                    tagRender={(users) => users[0]?.name ?? ''}
                    disableClearable={false}
                  />
                  <FormHelperText error>
                    <>{errors?.users || ' '}</>
                  </FormHelperText>
                </FormControl>
              </div>
              {warningText && <div className="mt-6 text-red-600 text-sm">Varning: {warningText}</div>}

              {lastRegisterRequestId && (
                <div className="mt-2 text-sm">
                  <a
                    href={`/registry-control/${lastRegisterRequestId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 underline"
                  >
                    Visa RegU-begäran
                  </a>
                </div>
              )}

              {secondaryWarningText && <div className="mt-2 text-red-600 text-sm">{secondaryWarningText}</div>}
            </DialogContent>
            <DialogActions className="!px-[20px] !py-[24px] !justify-between">
              <Button
                className="w-52 h-13"
                variant="outlined"
                color="error"
                onClick={() => {
                  resetState();
                  props.onClose();
                }}
              >
                Avbryt
              </Button>
              <Button
                className="w-52 h-13"
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting || !isValid || !dirty || disableCreateButton}
              >
                {forceCreate ? 'Skapa ändå' : 'Skapa'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateRegisterRequestDialog;
